/* eslint-disable @typescript-eslint/no-explicit-any */
// templateMessageSendSlice.ts
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { addEsChatToConfig } from "../../Utils/apiUtils";
import axios from 'axios';

interface WhatsappContact {
  input: string;
  wa_id: string;
}

interface WhatsappMessage {
  id: string;
  message_status: string;
}

interface WhatsappResponseInfo {
  contacts: WhatsappContact[];
  messages: WhatsappMessage[];
  messaging_product: string;
}

interface SystemUserPayload {
  to: string;
  type: string;
  template: {
    name: string;
    language: {
      code: string;
    };
    components: Array<{
      type: string;
      parameters?: Array<{
        text?: string;
        type?: string;
        payload?: string;
      }>;
      index?: string;
      sub_type?: string;
    }>;
  };
  recipient_type: string;
  messaging_product: string;
}

interface TemplateMessageResponse {
  id: number;
  status: string;
  channel: string;
  success: boolean;
  user_id: string;
  read_time: string | null;
  recipient: string;
  created_at: number;
  message_id: string;
  system_user: string;
  phone_number: number;
  delivered_time: string | null;
  recipient_payload: string;
  system_user_payload: SystemUserPayload;
  whatsapp_response_info: WhatsappResponseInfo;
  message?: string;
}

interface SendTemplateMessagePayload {
  template: string;
  variables: Record<string, string>;
  current_recipient: string;
  user_id: string;
  scheduled_time?: number;
}

interface TemplateMessageState {
  isSending: boolean;
  error: string | null;
  lastSentMessage: TemplateMessageResponse | null;
}

const initialState: TemplateMessageState = {
  isSending: false,
  error: null,
  lastSentMessage: null
};

export const sendTemplateMessage = createAsyncThunk<
  TemplateMessageResponse,
  SendTemplateMessagePayload,
  { rejectValue: string }
>(
  'templateMessage/send',
  async (payload, { rejectWithValue }) => {
    try {
      
      const config = addEsChatToConfig();
      
      const response = await axios.post('https://api.insightssystem.com/api:bVXsw_FD/web/messages/send/template', payload, config);
      const result = response.data as TemplateMessageResponse;
      
      // Check if the API call was successful
      if (!result.success) {
        // Extract error message from API response
        let errorMessage = "Message could not be sent. Please try again.";

        // First try to get error from the direct response structure
        if (result.message) {
          errorMessage = result.message;
        } else {
          // Try to extract error message from WhatsApp response based on available structure
          const whatsappResponse = result.whatsapp_response_info as any;
          
          if (whatsappResponse) {
            if (whatsappResponse.error_user_msg) {
              // First structure - has error_user_msg
              errorMessage = whatsappResponse.error_user_msg;
            } else if (whatsappResponse.error_data?.details) {
              // Second structure - has error_data.details
              errorMessage = whatsappResponse.error_data.details;
            } else if (whatsappResponse.message) {
              // Fallback to general message if available
              errorMessage = whatsappResponse.message;
            }
          }
        }
        
        return rejectWithValue(errorMessage);
      }
      
      return result;
    } catch (error: any) {
      // Handle axios errors with response data
      if (error.response) {
        const responseData = error.response.data;
        
        // Try to extract error message from the response
        if (responseData && responseData.message) {
          return rejectWithValue(responseData.message);
        }
        
        // Fallback to status text or generic message
        return rejectWithValue(error.response.statusText || `Request failed with status code ${error.response.status}`);
      }
      
      // Handle other errors
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('An unknown error occurred');
    }
  }
);

const templateMessageSendSlice = createSlice({
  name: 'templateMessage',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetSendState: (state) => {
      state.isSending = false;
      state.error = null;
      state.lastSentMessage = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendTemplateMessage.pending, (state) => {
        state.isSending = true;
        state.error = null;
      })
      .addCase(sendTemplateMessage.fulfilled, (state, action) => {
        state.isSending = false;
        state.error = null;
        state.lastSentMessage = action.payload;
      })
      .addCase(sendTemplateMessage.rejected, (state, action) => {
        state.isSending = false;
        state.error = action.payload ?? 'Failed to send template message';
      });
  },
});

// Selectors (avoid importing ../store — prevents circular import with messengerSlice)
type TemplateMessageRootPick = { templateMessage: TemplateMessageState };
export const selectIsSending = (state: TemplateMessageRootPick) =>
  state.templateMessage.isSending;
export const selectError = (state: TemplateMessageRootPick) =>
  state.templateMessage.error;
export const selectLastSentMessage = (state: TemplateMessageRootPick) =>
  state.templateMessage.lastSentMessage;

export const { clearError, resetSendState } = templateMessageSendSlice.actions;
export default templateMessageSendSlice.reducer;